import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Clock, FileText } from "lucide-react";
import EnquiryForm from "@/components/EnquiryForm";
import { COMPANY } from "@/constants/company";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

function RecentEnquiries() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const load = () => setItems(JSON.parse(localStorage.getItem("et_enquiries") || "[]").slice(0, 3));
    load();
    window.addEventListener("et-enquiry-saved", load);
    return () => window.removeEventListener("et-enquiry-saved", load);
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="mt-8 rounded-lg border border-navy-600 bg-navy-800 p-6" data-testid="recent-enquiries">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
        <FileText className="h-4 w-4 text-brand" /> Your recent enquiries
      </h3>
      <ul className="mt-4 space-y-3">
        {items.map((it, i) => (
          <li key={i} className="border-b border-navy-600/50 pb-3 text-sm last:border-0 last:pb-0" data-testid={`recent-enquiry-${i}`}>
            <p className="text-slate-200">{it.company} — {it.product_domain}</p>
            <p className="mt-0.5 font-mono text-xs text-slate-500">{new Date(it.created_at).toLocaleString()}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-24">
        <motion.p {...fadeUp} className="font-mono text-xs uppercase tracking-[0.35em] text-brand">Contact Us</motion.p>
        <motion.h1 {...fadeUp} className="mt-4 text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">
          Let's talk supply
        </motion.h1>
        <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-6 max-w-2xl text-base text-slate-300 md:text-lg">
          Send us your product specification, volume and destination port. Our trading desk will come back with
          pricing, availability and documentation details.
        </motion.p>

        <div className="mt-14 grid gap-12 lg:grid-cols-5">
          <motion.div {...fadeUp} className="lg:col-span-3">
            <div className="rounded-lg border border-navy-600 bg-navy-800/60 p-7 md:p-10">
              <h2 className="text-2xl font-bold text-white">Request a quote</h2>
              <div className="mt-8">
                <EnquiryForm />
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-2">
            <div className="space-y-5">
              <div className="rounded-lg border border-navy-600 bg-navy-800 p-6" data-testid="contact-address-card">
                <MapPin className="h-6 w-6 text-brand" />
                <h3 className="mt-4 font-semibold text-white">Head office</h3>
                <address className="mt-2 text-sm not-italic leading-relaxed text-slate-300">
                  {COMPANY.addressLines.map((l) => (
                    <span key={l} className="block">{l}</span>
                  ))}
                </address>
              </div>
              <div className="rounded-lg border border-navy-600 bg-navy-800 p-6" data-testid="contact-email-card">
                <Mail className="h-6 w-6 text-brand" />
                <h3 className="mt-4 font-semibold text-white">Email</h3>
                <a href={`mailto:${COMPANY.email}`} className="mt-2 block text-sm text-slate-300 transition-colors hover:text-brand" data-testid="contact-email-link">
                  {COMPANY.email}
                </a>
              </div>
              <div className="rounded-lg border border-navy-600 bg-navy-800 p-6" data-testid="contact-hours-card">
                <Clock className="h-6 w-6 text-brand" />
                <h3 className="mt-4 font-semibold text-white">Response time</h3>
                <p className="mt-2 text-sm text-slate-300">Enquiries are answered within one business day (SGT).</p>
              </div>
            </div>
            <RecentEnquiries />
          </motion.div>
        </div>

        <motion.div {...fadeUp} className="mt-14 overflow-hidden rounded-lg border border-navy-600" data-testid="contact-map">
          <iframe
            title="Excellent Traders Pte Ltd — 60 Paya Lebar Road, Paya Lebar Square 1, Singapore 409051"
            src="https://maps.google.com/maps?q=60%20Paya%20Lebar%20Road%2C%20Paya%20Lebar%20Square%201%2C%20Singapore%20409051&t=&z=16&ie=UTF8&iwloc=&output=embed"
            className="h-[380px] w-full"
            loading="lazy"
          />
        </motion.div>
      </section>
    </div>
  );
}
